const NULL_COUNTRY = 'ZZ';

function lookupAsnPrivateReserved(asnParam) {
  const asn = parseInt(asnParam);
  if (asnParam === null || asnParam === undefined || Number.isNaN(asn)) {
    return null;
  }

  // reservedRanges lists ranges of reserved ASNs. Taken from
  // https://www.iana.org/assignments/as-numbers/as-numbers.xhtml
  const reservedRanges = [
    [0, 0],
    [23456, 23456], // AS_TRANS
    [64496, 64511], // Reserved for use in documentation and sample code
    [65535, 65535],
    [65536, 65551], // Reserved for use in documentation and sample code
    [65552, 131071],
    [4294967295, 4294967295]
  ];

  // privateRanges lists ranges of private ASNs Taken from
  // https://www.iana.org/assignments/as-numbers/as-numbers.xhtml
  const privateRanges = [
    [64512, 65534],
    [4200000000, 4294967294]
  ];

  for (const r of reservedRanges) {
    if (asn < r[0]) {
      break; // not found, but give up early
    }
    if (asn >= r[0] && asn <= r[1]) {
      return `-Reserved AS-,${NULL_COUNTRY}`;
    }
  }
  for (const r of privateRanges) {
    if (asn < r[0]) {
      break; // not found, but give up early
    }
    if (asn >= r[0] && asn <= r[1]) {
      return `-Private Use AS-,${NULL_COUNTRY}`;
    }
  }

  return null;
}

const ASN_MAX = 4294967295;
const ASN_16_BIT_MAX = 65535;

// Supported AS number notations (RFC 5396)
const ASN_NOTATION = {
  ASPLAIN: 'asplain',
  ASDOT: 'asdot'
};

const ASDOT_REGEX = /^(\d{1,5})\.(\d{1,5})$/;
const ASPLAIN_REGEX = /^\d+$/;

function toAsnNumber(asnParam) {
  let asn;
  if (typeof asnParam === 'number') {
    asn = asnParam;
  } else if (typeof asnParam === 'string' && ASPLAIN_REGEX.test(asnParam.trim())) {
    // Parsed only after a strict digits-only check, since parseInt would partially
    // parse values such as '1.5', '12abc' or '1e3' into misleading AS numbers.
    asn = parseInt(asnParam, 10);
  } else {
    return null;
  }

  if (!Number.isInteger(asn) || asn < 0 || asn > ASN_MAX) {
    return null;
  }
  return asn;
}

/**
 * Converts an asplain (decimal) AS number to asdot notation, per RFC 5396.
 * AS numbers that fit in 16 bits are left in plain decimal form; 32-bit AS numbers
 * are rendered as `<high>.<low>` (e.g. 4266395598 -> '65100.1998').
 * Returns null when the input isn't a valid AS number.
 */
function asplainToAsdot(asnParam) {
  const asn = toAsnNumber(asnParam);
  if (asn === null) {
    return null;
  }
  if (asn <= ASN_16_BIT_MAX) {
    return `${asn}`;
  }
  return `${Math.floor(asn / 65536)}.${asn % 65536}`;
}

/**
 * Converts an asdot (or asdot+) AS number to its asplain (decimal) numeric value.
 * Plain decimal input is passed through. Returns null when the input isn't a valid AS number.
 */
function asdotToAsplain(asnParam) {
  if (typeof asnParam === 'number') {
    return toAsnNumber(asnParam);
  }
  if (typeof asnParam !== 'string') {
    return null;
  }

  const value = asnParam.trim();
  const match = value.match(ASDOT_REGEX);
  if (!match) {
    return toAsnNumber(value);
  }

  const high = parseInt(match[1], 10);
  const low = parseInt(match[2], 10);
  if (high > ASN_16_BIT_MAX || low > ASN_16_BIT_MAX) {
    return null;
  }
  return high * 65536 + low;
}

/**
 * Formats an AS number for display using the given notation, falling back to the
 * original value when it can't be parsed as an AS number.
 */
function formatAsn(asnParam, notation = ASN_NOTATION.ASPLAIN) {
  if (notation !== ASN_NOTATION.ASDOT) {
    return `${asnParam}`;
  }
  return asplainToAsdot(asnParam) ?? `${asnParam}`;
}

// query dimensions whose values are AS numbers
const ASN_DIMENSIONS = [
  'AS_src',
  'AS_dst',
  'src_nexthop_asn',
  'dst_nexthop_asn',
  'src_second_asn',
  'dst_second_asn',
  'src_third_asn',
  'dst_third_asn',
  'src_secondhop_asn_noprepend',
  'dst_secondhop_asn_noprepend',
  'src_thirdhop_asn_noprepend',
  'dst_thirdhop_asn_noprepend',
  'src_asn_generic',
  'dst_asn_generic',
  'src_as',
  'dst_as'
];

module.exports = {
  NULL_COUNTRY,
  ASN_DIMENSIONS,
  ASN_NOTATION,
  lookupAsnPrivateReserved,
  asplainToAsdot,
  asdotToAsplain,
  formatAsn
};
